import {
  type HttpInterceptorFn,
  type HttpRequest,
  type HttpHandlerFn,
  type HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject, filter, take, switchMap, catchError, finalize } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { LUMIO_APP_ROUTE_PREFIX } from '../constants/app-route-prefix';

/**
 * Sentinel values for refreshDone$:
 *   null    → refresh in progress (queue new requests)
 *   ''      → refresh failed (signal queued requests to abort)
 *   string  → new access token (retry queued requests)
 */
let isRefreshing = false;
const refreshDone$ = new BehaviorSubject<string | null>(null);

function addHeaders(req: HttpRequest<unknown>, token: string | null, lang: string): HttpRequest<unknown> {
  const headers: Record<string, string> = { 'Accept-Language': lang };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return req.clone({ setHeaders: headers });
}

function resolveAcceptLanguage(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'pt-BR';
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const lang = resolveAcceptLanguage();
  const token = auth.getSessionToken();

  // Never intercept the refresh endpoint — would cause an infinite loop.
  // Also skip the Bearer header on refresh: the expired access token would
  // cause the backend to reject the refresh request before it can rotate tokens.
  const isRefreshRequest = req.url.includes('/auth/refresh');

  return next(addHeaders(req, isRefreshRequest ? null : token, lang)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401 || isRefreshRequest) {
        return throwError(() => err);
      }

      // A refresh is already in flight — queue this request.
      // '' = refresh failed, non-empty string = new token.
      if (isRefreshing) {
        return refreshDone$.pipe(
          filter((t) => t !== null),
          take(1),
          switchMap((newToken) => {
            if (newToken === '') {
              return throwError(() => err);
            }
            return next(addHeaders(req, newToken, lang));
          }),
        );
      }

      // Start the refresh.
      isRefreshing = true;
      refreshDone$.next(null);

      return auth.refreshAccessToken().pipe(
        switchMap((newToken) => {
          refreshDone$.next(newToken);        // unblock queued requests with new token
          refreshDone$.next(null);            // reset sentinel for next refresh cycle
          return next(addHeaders(req, newToken, lang));
        }),
        catchError((refreshErr) => {
          refreshDone$.next('');             // signal queued requests to abort
          refreshDone$.next(null);
          auth.clearLocalSession();
          void router.navigate([`${LUMIO_APP_ROUTE_PREFIX}/welcome`], { replaceUrl: true });
          return throwError(() => refreshErr);
        }),
        finalize(() => {
          // Runs on success, error, AND cancellation (unsubscribe).
          // Guards against isRefreshing getting stuck true when the subscriber
          // (e.g. a destroyed component) cancels before the refresh completes.
          isRefreshing = false;
        }),
      );
    }),
  );
};
